import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Card, CardContent } from '../../../components/ui';
import { useLanguageStore } from '../../../store/languageStore';
import { ROOM_POLLING_INTERVAL_MS } from '../../../utils/polling';
import { getUITranslation } from '../i18n/translations';
import { useKanbanBoardStore } from '../store/kanbanBoardStore';
import { KanbanBoardView } from '../components/KanbanBoardView';
import { KanbanActivityModal } from '../components/KanbanActivityModal';
import { getRoom, createBoard, deleteBoard } from '../services/api';
import type { KanbanActivity, KanbanBoard } from '../types';

export const Room = () => {
  const { roomId } = useParams<{ roomId: string }>();
  const navigate = useNavigate();
  const { language } = useLanguageStore();
  const t = getUITranslation(language);

  const { roomInfo, activeBoardId, setRoomInfo, setActiveBoardId } = useKanbanBoardStore();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copyFeedback, setCopyFeedback] = useState(false);
  const [filterMyTasks, setFilterMyTasks] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState<KanbanActivity | null>(null);

  // Create board form
  const [showCreateBoard, setShowCreateBoard] = useState(false);
  const [newBoardName, setNewBoardName] = useState('');
  const [newBoardColumns, setNewBoardColumns] = useState('');
  const [newBoardTaskTypes, setNewBoardTaskTypes] = useState('');
  const [creatingBoard, setCreatingBoard] = useState(false);

  const userEmail = useMemo(() => sessionStorage.getItem('userEmail'), []);
  const adminToken = useMemo(() => sessionStorage.getItem('adminToken'), []);
  const userToken = useMemo(() => sessionStorage.getItem('userToken'), []);
  const userId = useMemo(() => sessionStorage.getItem('userId'), []);

  const isUserTokenAdmin = (() => {
    if (!userToken) return false;
    try {
      const decodedToken = atob(userToken);
      const tokenParts = decodedToken.split(':');
      return tokenParts[2] === 'true';
    } catch {
      return false;
    }
  })();

  const adminAccessToken = adminToken || (isUserTokenAdmin ? userToken : null);
  const isAdmin = !!adminAccessToken;

  const userName = useMemo(() => {
    if (!roomInfo) return null;
    const participant = roomInfo.participants.find((p) => p.userId === userId);
    return participant?.name ?? userId;
  }, [roomInfo, userId]);

  const activeBoard = useMemo(
    () => roomInfo?.boards.find((b) => b.id === activeBoardId) ?? null,
    [roomInfo, activeBoardId]
  );

  const fetchRoom = async () => {
    if (!roomId) return;
    try {
      const room = await getRoom(roomId);
      setRoomInfo(room);
      setLoading(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load room');
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!userEmail) {
      navigate('/apps/kanban-board');
      return;
    }
    fetchRoom();
  }, [roomId, userEmail, navigate]);

  useEffect(() => {
    if (!roomId || loading || error) return;
    const interval = setInterval(fetchRoom, ROOM_POLLING_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [roomId, loading, error]);

  // Keep active board in sync when roomInfo updates
  useEffect(() => {
    if (!roomInfo || !activeBoardId) return;
    const stillExists = roomInfo.boards.some((b) => b.id === activeBoardId);
    if (!stillExists) setActiveBoardId(null);
  }, [roomInfo, activeBoardId, setActiveBoardId]);

  // Keep selectedActivity in sync when board updates
  useEffect(() => {
    if (!selectedActivity || !activeBoard) return;
    const updated = activeBoard.activities.find((a) => a.id === selectedActivity.id);
    if (updated) setSelectedActivity(updated);
    else setSelectedActivity(null);
  }, [activeBoard]);

  const handleCopyRoomLink = async () => {
    if (!roomId) return;
    const joinUrl = `${window.location.origin}/apps/kanban-board/join?code=${roomId}`;
    try {
      await navigator.clipboard.writeText(joinUrl);
      setCopyFeedback(true);
      setTimeout(() => setCopyFeedback(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleCreateBoard = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newBoardName.trim() || !adminAccessToken || !roomId) return;
    setCreatingBoard(true);
    try {
      const columns = newBoardColumns
        .split(',')
        .map((c) => c.trim())
        .filter(Boolean);
      const taskTypes = newBoardTaskTypes
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean);
      const assignedUsers = roomInfo?.participants.map((p) => p.userId) ?? [];
      const updatedRoom = await createBoard(
        roomId,
        {
          name: newBoardName.trim(),
          columns: columns.length > 0 ? columns : undefined,
          assignedUsers,
          taskTypes: taskTypes.length > 0 ? taskTypes : undefined,
        },
        adminAccessToken
      );
      setRoomInfo(updatedRoom);
      setNewBoardName('');
      setNewBoardColumns('');
      setNewBoardTaskTypes('');
      setShowCreateBoard(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create board');
    } finally {
      setCreatingBoard(false);
    }
  };

  const handleDeleteBoard = async (boardId: string) => {
    if (!adminAccessToken || !roomId) return;
    if (!window.confirm(t.room.deleteBoard + '?')) return;
    try {
      const updatedRoom = await deleteBoard(roomId, boardId, adminAccessToken);
      setRoomInfo(updatedRoom);
      if (activeBoardId === boardId) setActiveBoardId(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete board');
    }
  };

  const handleBoardUpdate = (updatedBoard: KanbanBoard) => {
    if (!roomInfo) return;
    const updatedBoards = roomInfo.boards.map((b) =>
      b.id === updatedBoard.id ? updatedBoard : b
    );
    setRoomInfo({ ...roomInfo, boards: updatedBoards });
  };

  const handleActivityUpdate = (updatedActivity: KanbanActivity) => {
    if (!roomInfo || !activeBoard) return;
    const updatedActivities = activeBoard.activities.map((a) =>
      a.id === updatedActivity.id ? updatedActivity : a
    );
    handleBoardUpdate({ ...activeBoard, activities: updatedActivities });
    setSelectedActivity(updatedActivity);
  };

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="text-center">
          <div className="mb-4 text-4xl">📋</div>
          <p className="text-muted-foreground">Loading room...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="text-center">
          <p className="mb-4 text-destructive">{error}</p>
          <Button onClick={() => navigate('/apps/kanban-board')}>Go Back</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">{roomInfo?.name}</h1>
          {activeBoard && (
            <p className="mt-1 text-sm font-semibold text-muted-foreground">
              {activeBoard.name}
            </p>
          )}
          <p className="text-sm text-muted-foreground">
            {roomInfo?.participants.length ?? 0} {t.room.players}
          </p>
          <div className="mt-3 flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Room Code:</span>
            <div className="flex items-center gap-2 rounded-md bg-muted px-3 py-1">
              <code className="font-mono text-sm font-semibold">{roomId ?? ''}</code>
              <button
                onClick={handleCopyRoomLink}
                className="ml-2 inline-flex items-center gap-1 text-xs text-primary hover:text-primary/80 cursor-pointer"
                title="Copy join link"
              >
                📋
                {copyFeedback && <span className="text-xs">{t.room.linkCopied}</span>}
              </button>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {activeBoard && (
            <>
              <Button
                variant={filterMyTasks ? undefined : 'outline'}
                size="sm"
                onClick={() => setFilterMyTasks((v) => !v)}
              >
                {t.room.filterMyTasks}
              </Button>
              <Button variant="outline" size="sm" onClick={() => setActiveBoardId(null)}>
                ← {t.room.backToBoards}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="mb-4 rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {/* Board view or boards list */}
      {activeBoard ? (
        <KanbanBoardView
          board={activeBoard}
          roomId={roomId ?? ''}
          userId={userId}
          userName={userName}
          userEmail={userEmail}
          filterMyTasks={filterMyTasks}
          t={t.room}
          onActivityClick={setSelectedActivity}
          onBoardUpdate={handleBoardUpdate}
        />
      ) : (
        <div>
          <div className="mb-6 flex items-center justify-between">
            <h2 className="text-xl font-semibold">{t.room.boards}</h2>
            {isAdmin && (
              <Button size="sm" onClick={() => setShowCreateBoard((v) => !v)}>
                {t.room.createBoard}
              </Button>
            )}
          </div>

          {isAdmin && showCreateBoard && (
            <Card className="mb-6 border-2 bg-gradient-to-br from-primary/10 via-transparent to-secondary/10">
              <CardContent className="p-6">
                <form onSubmit={handleCreateBoard} className="space-y-4">
                  <div>
                    <label className="mb-1 block text-sm font-semibold">{t.room.boardName}</label>
                    <input
                      type="text"
                      value={newBoardName}
                      onChange={(e) => setNewBoardName(e.target.value)}
                      placeholder={t.room.boardNamePlaceholder}
                      required
                      disabled={creatingBoard}
                      className="h-11 w-full rounded-lg border border-border/80 bg-background/80 px-4 focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-semibold">{t.room.columns}</label>
                    <input
                      type="text"
                      value={newBoardColumns}
                      onChange={(e) => setNewBoardColumns(e.target.value)}
                      placeholder={t.room.defaultColumns}
                      disabled={creatingBoard}
                      className="h-11 w-full rounded-lg border border-border/80 bg-background/80 px-4 focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
                    />
                    <p className="mt-1 text-xs text-muted-foreground">
                      Comma-separated column names
                    </p>
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-semibold">{t.room.taskTypes}</label>
                    <input
                      type="text"
                      value={newBoardTaskTypes}
                      onChange={(e) => setNewBoardTaskTypes(e.target.value)}
                      placeholder={t.room.taskTypesPlaceholder}
                      disabled={creatingBoard}
                      className="h-11 w-full rounded-lg border border-border/80 bg-background/80 px-4 focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
                    />
                    <p className="mt-1 text-xs text-muted-foreground">
                      Comma-separated task types
                    </p>
                  </div>
                  <div className="flex gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowCreateBoard(false)}
                      disabled={creatingBoard}
                    >
                      {t.room.cancel}
                    </Button>
                    <Button type="submit" disabled={creatingBoard || !newBoardName.trim()}>
                      {creatingBoard ? 'Creating...' : t.room.createBoard}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          {roomInfo?.boards.length === 0 ? (
            <div className="flex min-h-[200px] flex-col items-center justify-center rounded-2xl border border-dashed">
              <div className="mb-2 text-4xl">📋</div>
              <p className="text-muted-foreground">{t.room.noBoards}</p>
              {isAdmin && (
                <p className="text-sm text-muted-foreground">{t.room.addFirstBoard}</p>
              )}
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {roomInfo?.boards.map((board) => (
                <Card
                  key={board.id}
                  className="group cursor-pointer border-2 transition-all hover:border-primary/40 hover:shadow-lg"
                  onClick={() => setActiveBoardId(board.id)}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold">{board.name}</h3>
                        <p className="mt-1 text-xs text-muted-foreground">
                          {board.activities.length} tasks · {board.columns.length} columns
                        </p>
                      </div>
                      {isAdmin && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteBoard(board.id);
                          }}
                          className="rounded p-1 text-muted-foreground opacity-0 transition-opacity hover:text-destructive group-hover:opacity-100"
                        >
                          🗑
                        </button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Activity detail modal */}
      {selectedActivity && activeBoard && (
        <KanbanActivityModal
          activity={selectedActivity}
          board={activeBoard}
          roomId={roomId ?? ''}
          isAdmin={isAdmin}
          userId={userId}
          userName={userName}
          t={t.room}
          onClose={() => setSelectedActivity(null)}
          onBoardUpdate={handleBoardUpdate}
          onActivityUpdate={handleActivityUpdate}
        />
      )}
    </div>
  );
};
